// Emergency Cart Request Deduplication System
// This prevents multiple simultaneous requests to the same cart endpoint

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class CartRequestDeduplicator {
  private pendingRequests = new Map<string, PendingRequest>();
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 2000; // 2 seconds cache
  private readonly REQUEST_TIMEOUT = 10000; // 10 seconds timeout

  // Deduplicate cart requests by key
  async dedupRequest<T>(
    key: string, 
    requestFn: () => Promise<T>,
    useCache: boolean = true
  ): Promise<T> {
    
    // Check cache first (if enabled)
    if (useCache) {
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.data as T;
      }
    }

    // Check if request is already pending
    const pending = this.pendingRequests.get(key);
    if (pending) {
      // Check if request is not too old (timeout protection)
      if (Date.now() - pending.timestamp < this.REQUEST_TIMEOUT) {
        return pending.promise as Promise<T>;
      } else {
        // Clean up old request
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = requestFn();
    
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    try {
      const result = await promise;
      
      // Cache the result
      if (useCache) {
        this.cache.set(key, {
          data: result,
          timestamp: Date.now()
        });
      }

      return result;
    } catch (error) {
      throw error;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(key);
    }
  }

  // Clear cache for specific key or all
  clearCache(key?: string) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Clear old cache entries
  cleanupCache() {
    const now = Date.now();
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      const cached = this.cache.get(key);
      if (cached && now - cached.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }

  // Get stats for monitoring
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      cacheEntries: this.cache.size,
      cacheKeys: Array.from(this.cache.keys())
    };
  }

  // Emergency method to clear everything on critical events
  emergencyReset() {
    this.pendingRequests.clear();
    this.cache.clear();
  }
}

// Global instance
export const cartDeduplicator = new CartRequestDeduplicator();

declare global {
  var __kraahlCartDedupCleanupTimer: ReturnType<typeof setInterval> | undefined;
  var __kraahlCartDedupCleanupFn: (() => void) | undefined;
}

globalThis.__kraahlCartDedupCleanupFn = () => {
  cartDeduplicator.cleanupCache();
};

if (typeof window !== "undefined" && !globalThis.__kraahlCartDedupCleanupTimer) {
  globalThis.__kraahlCartDedupCleanupTimer = setInterval(() => {
    globalThis.__kraahlCartDedupCleanupFn?.();
  }, 30000);
}

// Utility functions for common cart operations
export const dedupCartRequest = async <T>(
  userId: string | null,
  guestId: string | null,
  requestFn: () => Promise<T>
): Promise<T> => {
  const key = userId ? `cart-${userId}` : `cart-guest-${guestId}`;
  return cartDeduplicator.dedupRequest(key, requestFn, true);
};

export const dedupSyncLoginRequest = async <T>(
  userId: string,
  guestId: string | null,
  requestFn: () => Promise<T>
): Promise<T> => {
  const key = `sync-login-${userId}-${guestId}`;
  return cartDeduplicator.dedupRequest(key, requestFn, false); // No cache for sync operations
};

export const dedupAvailabilityRequest = async <T>(
  listingId: string,
  requestFn: () => Promise<T>,
  useCache: boolean = true
): Promise<T> => {
  const key = `availability-${listingId}`;
  return cartDeduplicator.dedupRequest(key, requestFn, useCache);
};

// Clear cart cache when needed
export const clearCartCache = (userId?: string, guestId?: string) => {
  if (userId) {
    cartDeduplicator.clearCache(`cart-${userId}`);
  }
  if (guestId) {
    cartDeduplicator.clearCache(`cart-guest-${guestId}`);
  }
};

// Emergency function to clear all cart-related state
export const emergencyResetCartSystem = () => {
  cartDeduplicator.emergencyReset();
  // Emergency reset completed
};
